import { useEffect, useState } from "react";
import {
  fetchUser,
  getAuthToken,
  redirectToLogin,
  registerGlobalUserUpdateCallback,
} from "services/authService";
import UserContext from "./UserContext";

const enhanceUser = (user_data) => {
  if (!user_data) {
    return null;
  }

  return {
    ...user_data,
    hasRole: (role) =>
      Array.isArray(role)
        ? role.some((r) => user_data.role_names?.includes(r))
        : user_data.role_names?.includes(role),
    hasPermission: (permission) =>
      Array.isArray(permission)
        ? permission.some((p) => user_data.permission_names?.includes(p))
        : user_data.permission_names?.includes(permission),
  };
};

// Provider component that wraps the app and makes auth object available to
// any child component that calls useUser().
export function UserProvider({ children }) {
  const [user, setLocalUserState] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);

        // Register the context's user state setter with the authService
        // This allows authService to update the context when user data changes
        // (e.g., after background refresh)
        registerGlobalUserUpdateCallback(setLocalUserState);

        // Get auth token - this will attempt to use the refresh token cookie if needed
        const token = await getAuthToken();
        if (!token) {
          setError(new Error("Authentication failed - no valid token"));
          redirectToLogin();
          return;
        }

        setAuthToken(token);

        // Get user data
        const user_data = await fetchUser();
        if (!user_data) {
          setError(new Error("Authentication failed - no user data"));
          redirectToLogin();
          return;
        }

        // setLocalUserState(user_data);

        // User-Objekt um Methoden erweitern
        const enhanced_user = enhanceUser(user_data);

        setLocalUserState(enhanced_user);
        setError(null);
      } catch (err) {
        setError(err);
        redirectToLogin();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Create a function to refresh user data if needed
  const refreshUser = async () => {
    try {
      setLoading(true);
      const user_data = await fetchUser();
      const enhanced_user = enhanceUser(user_data);
      setLocalUserState(enhanced_user);
      return enhanced_user;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setUser = (user) => {
    setLocalUserState(user);
  };

  // Update auth token if needed
  const refreshToken = async () => {
    try {
      const token = await getAuthToken();
      setAuthToken(token);
      return token;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  // The value that will be supplied to any consuming components
  const value = {
    user,
    authToken,
    loading,
    error,
    setUser,
    refreshUser,
    refreshToken,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
